import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";
import {
  ArrowLeft,
  EllipsisVertical,
  Inbox,
  Mic,
  Phone,
  PhoneIncoming,
  Play,
  Voicemail,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import { HeroVorgangActionFooter } from "./hero-vorgang-action-footer";
import { HeroVorgangDetailRail } from "./hero-vorgang-detail-rail";
import type { HeroVorgangRailData } from "./hero-vorgang-detail-rail";

function anrufRail(t: Translate): HeroVorgangRailData {
  return {
    externalId: "#2026-23901",
    title: t("anrufMarkusVogt8211"),
    statusLabel: "TO-DO",
    assignee: { initials: "NH", name: t("ninaHartmann"), bg: ld("#0B0F19", "#fafafa") },
    board: { name: t("telefonanlage2"), icon: "inbox" },
    recipient: { name: t("markusVogt"), source: t("anruf") },
    property: { name: t("ahornweg5"), unit: "WE 7 · EG" },
    openPickers: ["due", "tags", "description"],
  };
}

/**
 * KI-Anrufbeantworter module — full-bleed window interior (no 220px sidebar).
 * A voicemail-derived Vorgang on the left + a detail rail on the right, with a
 * floating "Aus der Telefonanlage" callout overlaying the bottom of the window.
 * Ported 1:1 from the prototype (sc-if isAnrufbeantworter, content + overlay).
 */
export function ModuleAnrufbeantworter(_props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleAnrufbeantworter");
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        height: MODULE_HEIGHT,
        background: ld("#fff", "#181b1f"),
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          background: ld("#fff", "#181b1f"),
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* VORGANG */}
          <div
            style={{
              position: "relative",
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              background: ld("#fff", "#181b1f"),
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "13px 18px",
                borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <ArrowLeft width={18} height={18} strokeWidth={1.8} />
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 15,
                  fontWeight: 700,
                  color: ld("#0B0F19", "#fafafa"),
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    color: H.accent,
                    display: "inline-flex",
                    flexShrink: 0,
                  }}
                >
                  <PhoneIncoming width={16} height={16} strokeWidth={1.9} />
                </span>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t("anrufMarkusVogt8211")}
                </span>
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  display: "inline-flex",
                  color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <EllipsisVertical width={18} height={18} strokeWidth={1.8} />
              </span>
            </div>
            <div
              style={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  padding: "16px 18px",
                  paddingBottom: 56,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: H.accentSoft,
                    border: `1px solid ${H.accentBorder}`,
                    borderRadius: 12,
                    padding: "11px 14px",
                  }}
                >
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      background: H.accent,
                      color: ld("#fff", "#181b1f"),
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Phone width={16} height={16} strokeWidth={1.9} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: H.label,
                        fontSize: 10,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: H.accent,
                        fontWeight: 700,
                      }}
                    >
                      {t("anruf")}
                    </div>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: ld("#0B0F19", "#fafafa"),
                        marginTop: 1,
                      }}
                    >
                      {t("k491512240187183")}
                    </div>
                  </div>
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: ld("#EAEFE9", "#2f3236"),
                      color: ld("#5E7C66", "#a5a9ae"),
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {t("mv")}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 16,
                    border: `1px solid ${ld("#ECEDF0", "#2f3236")}`,
                    borderRadius: 12,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      padding: "11px 14px",
                      borderBottom: `1px solid ${ld("#F3F4F6", "#292c30")}`,
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: ld("#EAEFE9", "#2f3236"),
                        color: ld("#5E7C66", "#a5a9ae"),
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      {t("mv")}
                    </span>
                    <span
                      style={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: ld("#0B0F19", "#fafafa"),
                      }}
                    >
                      {t("markusVogt")}
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "2px 8px",
                        border: `1px solid ${H.accentBorder}`,
                        borderRadius: 6,
                        fontSize: 11,
                        color: H.accent,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      <PhoneIncoming width={11} height={11} strokeWidth={1.9} />
                      {t("eingehenderAnruf")}
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 11.5,
                        color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
                      }}
                    >
                      {t("k1406202609")}
                    </span>
                  </div>
                  <div
                    style={{
                      padding: "13px 15px",
                      fontSize: 13,
                      lineHeight: 1.7,
                      color: ld("#2A2F3A", "#e0e4e9"),
                    }}
                  >
                    <div>
                      {t.rich("anrufVonNummer", {
                        dim: (chunks: ReactNode): ReactNode => (
                          <span style={{ color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)") }}>
                            {chunks}
                          </span>
                        ),
                      })}
                    </div>
                    <div>
                      {t.rich("angerufeneNummerZentrale", {
                        dim: (chunks: ReactNode): ReactNode => (
                          <span style={{ color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)") }}>
                            {chunks}
                          </span>
                        ),
                      })}
                    </div>
                    <div
                      style={{
                        marginTop: 9,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontWeight: 600,
                        color: ld("#0B0F19", "#fafafa"),
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background: H.accentSoft,
                          color: H.accent,
                        }}
                      >
                        <Play width={12} height={12} strokeWidth={2} />
                      </span>
                      {t("sprachnachricht018")}
                    </div>
                    <div
                      style={{
                        marginTop: 9,
                        paddingLeft: 2,
                        color: ld("#3D3A34", "#d5d9de"),
                      }}
                    >
                      {t("k8222GutenTagHier")}
                    </div>
                  </div>
                </div>
              </div>
              <HeroVorgangActionFooter />
            </div>
          </div>

          <HeroVorgangDetailRail rail={anrufRail(t)} />
        </div>
      </div>
    </div>
  );
}

/**
 * The "Aus der Telefonanlage" floating bar (frame-level overlay), prototype
 * line 1869. Positions itself absolutely relative to the aurora frame so it
 * overflows the window (rendered via the registry `Overlay` slot).
 */
export function AnrufbeantworterOverlay(): ReactNode {
  const t = useTranslations("marketing.moduleAnrufbeantworter");
  return (
    <div
      style={{
        position: "absolute",
        left: 16,
        right: 16,
        bottom: 16,
        background: ld("#fff", "#181b1f"),
        border: `1.5px solid ${ld("#E2E5EA", "#36393d")}`,
        borderRadius: 14,
        boxShadow:
          `0 38px 76px -22px ${ld("rgba(11,15,25,0.50)", "rgba(0,0,0,0.8)")}, 0 14px 30px -12px ${ld("rgba(11,15,25,0.30)", "rgba(0,0,0,0.5)")}`,
        zIndex: 30,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "15px 18px",
        }}
      >
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            background: ld("#EDEFF2", "#2d3034"),
            color: ld("#5B6472", "#b2b6bb"),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12.5,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {t("mv")}
        </span>
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: ld("#0B0F19", "#fafafa"),
              whiteSpace: "nowrap",
            }}
          >
            {t.rich("anruferNameUndNummer", {
              dim: (chunks: ReactNode): ReactNode => (
                <span style={{ color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"), fontWeight: 500 }}>
                  {chunks}
                </span>
              ),
            })}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 3,
              whiteSpace: "nowrap",
              fontSize: 12.5,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                color: H.accent,
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: 2,
                cursor: "pointer",
              }}
            >
              <Voicemail width={14} height={14} strokeWidth={2} />
              {t("nachrichtAbspielen")}
            </span>
            <span style={{ color: ld("rgba(11,15,25,0.3)", "rgba(250,250,250,0.3)") }}>{t("k183")}</span>
            <span style={{ color: ld("#C0453E", "#ff7e73"), fontWeight: 500 }}>
              {t("nichtErreicht")}
            </span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            height: 32,
            padding: "0 12px",
            border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
            borderRadius: 9,
            fontSize: 12.5,
            color: ld("rgba(11,15,25,0.6)", "rgba(250,250,250,0.6)"),
            flexShrink: 0,
          }}
        >
          <Inbox width={14} height={14} strokeWidth={1.8} />
          {t("anruf491512240187")}
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 30,
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
            fontSize: 13,
            fontStyle: "italic",
          }}
        >
          <Mic
            width={14}
            height={14}
            strokeWidth={1.8}
            style={{ flexShrink: 0 }}
          />
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("k8222GutenTagHier2")}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 12.5,
              color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
              whiteSpace: "nowrap",
            }}
          >
            {t("k14060912")}
          </span>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: ld("#0B0F19", "#fafafa"),
              color: ld("#fff", "#181b1f"),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {t("nh")}
          </span>
        </div>
      </div>
    </div>
  );
}
